import type { PortalNotification } from './notificationHref';
import { pendingAcknowledgementKey } from './notificationSessionCache.ts';

export type NotificationSource = 'live' | 'recovered';
export type NotificationPresentation = boolean | 'acknowledge';

/** Dependencies supplied by the root provider so delivery can be tested without React or Pusher. */
type DeliveryOptions = {
  userId: number;
  list: () => Promise<PortalNotification[]>;
  acknowledge: (id: string) => Promise<void>;
  // Return "acknowledge" when a successfully displayed toast must never replay.
  // Return true when acknowledgement should wait for a later user action.
  present: (notification: PortalNotification, source: NotificationSource) => NotificationPresentation;
  onAcknowledged?: (id: string) => void;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  alreadyShown?: string[];
  alreadyAcknowledged?: string[];
};

/**
 * Coordinate live events, inbox recovery, toast deduplication, and DELETE retries
 * for one authenticated user. Ordinary toasts can acknowledge as soon as they
 * are displayed, while decision/inbox items wait for a user action.
 *
 * `alreadyShown` prevents repeated toasts after a reload in the same tab.
 * Pending acknowledgements contain notifications already displayed or viewed.
 * @param options API, display, storage, and user dependencies for one login.
 * @returns Methods for live delivery, recovery, user acknowledgement, and cleanup.
 */
export function createNotificationDelivery(options: DeliveryOptions) {
  const storageKey = pendingAcknowledgementKey(options.userId);
  const pending = new Map<string, PortalNotification>();
  const seen = new Set(options.alreadyShown ?? []);
  const acknowledged = new Set(options.alreadyAcknowledged ?? []);
  const acknowledging = new Set<string>();
  let disposed = false;
  let recovering = false;

  try {
    const saved = JSON.parse(options.storage.getItem(storageKey) ?? '[]');
    if (Array.isArray(saved)) {
      for (const item of saved as PortalNotification[]) {
        if (item && typeof item.id === 'string') pending.set(item.id, item);
      }
    }
  } catch {
    // An unavailable or malformed local store must not stop backend recovery.
  }

  const savePending = () => {
    try {
      if (pending.size) options.storage.setItem(storageKey, JSON.stringify(Array.from(pending.values())));
      else options.storage.removeItem(storageKey);
    } catch {
      // The backend retains an unacknowledged notification for later recovery.
    }
  };

  /** Delete a displayed/viewed item once; store failures for reconnect or timer retries. */
  const acknowledgeViewed = async (notification: PortalNotification) => {
    if (disposed || acknowledged.has(notification.id) || acknowledging.has(notification.id)) return;
    acknowledging.add(notification.id);
    try {
      await options.acknowledge(notification.id);
      acknowledged.add(notification.id);
      pending.delete(notification.id);
      savePending();
    } catch {
      if (!disposed) {
        pending.set(notification.id, notification);
        savePending();
      }
    } finally {
      acknowledging.delete(notification.id);
    }
    if (!disposed && acknowledged.has(notification.id)) options.onAcknowledged?.(notification.id);
  };

  /** Present a new id once; a rejected toast can be presented again later. */
  const showNotification = (notification: PortalNotification, source: NotificationSource) => {
    if (disposed || !notification?.id) return;
    if (seen.has(notification.id)) return;
    seen.add(notification.id);
    try {
      const accepted = options.present(notification, source);
      if (!accepted) seen.delete(notification.id);
      else if (accepted === 'acknowledge') void acknowledgeViewed(notification);
    } catch {
      seen.delete(notification.id);
    }
  };

  /** Fetch outstanding backend fallbacks on login, startup, or reconnection. */
  const recover = async () => {
    if (disposed || recovering) return;
    recovering = true;
    try {
      const notifications = await options.list();
      if (disposed) return;
      for (const notification of notifications) showNotification(notification, 'recovered');
      retryPending();
    } finally {
      recovering = false;
    }
  };

  /** Retry DELETE only for items previously displayed or viewed by the user. */
  const retryPending = () => {
    if (disposed) return;
    for (const notification of Array.from(pending.values())) {
      void acknowledgeViewed(notification);
    }
  };

  return { showNotification, acknowledgeViewed, recover, retryPending, dispose: () => { disposed = true; } };
}
